/**
 * Rowted Admin's own fetch — plan §6.12, addendum §C.5.
 *
 * Separate from `lib/api.ts` for the same reason the router and the sessions
 * are separate: an admin request carries a different CSRF header, never an
 * `Idempotency-Key` (nothing here is replayed from an offline queue), and
 * never the `?scope=` a tenant request carries, because Rowted Admin has no
 * branches to be scoped to. Sharing one client and switching on a flag would
 * put a tenant's scope on a request that reads every tenant.
 *
 * `adminRequest` has the same shape as `request` in `lib/api.ts`, which is
 * what lets the list hooks take it as `options.request`
 * (`docs/rowted-admin-v2.md` §2.8): every URL they build then lands under
 * `/api/v1/platform` without a line of the list engine knowing.
 */
import { ApiError, type ApiErrorBody, type ErrorCode } from '@rowted/shared'

const CSRF_HEADER = 'x-rowted-platform-csrf'
const CSRF_KEY = 'rowted.admin.csrf'

/*
 * The CSRF token outlives a page load, so it is kept where a reload can find
 * it. sessionStorage rather than localStorage: it is scoped to this tab and
 * goes when the tab does, which matches a console session better than one
 * that survives until somebody clears their browser.
 */
let csrfToken: string | null = read()

function read(): string | null {
  try {
    return window.sessionStorage.getItem(CSRF_KEY)
  } catch {
    return null
  }
}

export function setAdminCsrfToken(token: string | null): void {
  csrfToken = token
  try {
    if (token) window.sessionStorage.setItem(CSRF_KEY, token)
    else window.sessionStorage.removeItem(CSRF_KEY)
  } catch { /* a private window; the token still works for this page load */ }
}

export interface AdminRequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
}

/**
 * Fetching `/me` to recover the CSRF token, at most once at a time.
 *
 * `sessionStorage` is per tab, so a second tab has the cookie and no token —
 * and then every POST is refused and the screen sits there empty. `/me`
 * returns the token for exactly this; the note on that route says why it is
 * safe on a GET.
 *
 * The shared promise matters: a list screen fires its rows, its totals and
 * its cards at once, and three parallel `/me` calls to recover one token
 * would be three where one will do.
 */
let recovering: Promise<void> | null = null

async function recoverCsrf(): Promise<void> {
  recovering ??= (async () => {
    try {
      const response = await fetch('/api/v1/platform/me', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      })
      if (!response.ok) return
      const me = await response.json() as { csrfToken?: string }
      if (me.csrfToken) setAdminCsrfToken(me.csrfToken)
    } catch { /* offline, or signed out; the caller's own error says so */ } finally {
      recovering = null
    }
  })()
  return recovering
}

export async function adminRequest<T>(
  path: string, options: AdminRequestOptions = {},
): Promise<T> {
  const method = options.method ?? 'GET'

  /*
   * Before a mutation, not after it fails. `useList` posts on mount, in
   * parallel with the `/me` the page is loading anyway, so waiting for a 403
   * to tell us would mean the first paint of a fresh tab is always an error.
   */
  if (method !== 'GET' && !csrfToken && !path.startsWith('/auth/')) {
    await recoverCsrf()
  }

  const headers: Record<string, string> = { Accept: 'application/json' }
  if (options.body !== undefined) headers['Content-Type'] = 'application/json'
  if (method !== 'GET' && csrfToken) headers[CSRF_HEADER] = csrfToken

  const response = await fetch(`/api/v1/platform${path}`, {
    method,
    headers,
    credentials: 'same-origin',
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  })

  if (response.status === 204) return undefined as T
  const payload = await response.json().catch(() => null)

  if (!response.ok) {
    const body = payload as ApiErrorBody | null
    throw new ApiError(
      (body?.code ?? 'internal_error') as ErrorCode,
      body?.message ?? 'Something went wrong.',
      { field: body?.field, details: body?.details, status: response.status },
    )
  }

  return payload as T
}
